import React, {createContext, ReactNode, useContext, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import {AvailableMenu, Menu, Product} from "@/data/entities/menu";
import {FulfillmentType, RestaurantLocation} from "@/data/entities/restaurant";
import {api} from "@/data/api";
import {filterAndSortOnAvailability, filterOnSearch} from "@/data/entities/menu/utils";
import ProductInfo from "@widgets/pos/order/menu/product/ProductInfo";
import {createMenuLookup, MenuLookup} from "@/data/entities/cart/utils";

export type ProductInfoToShow = {
    product: Product | null,
};

export type ShowProductInfo = ProductInfoToShow & {
    show: boolean,
};

type ExposedMenu = {
    availableMenu: AvailableMenu,
};

const convertMenuToExposed = (
    menu: Menu,
    fulfillmentType: FulfillmentType,
    selectedRestaurant: RestaurantLocation,
): ExposedMenu => {
    const availableMenu: AvailableMenu = filterAndSortOnAvailability(menu, fulfillmentType, selectedRestaurant.id)
    return {availableMenu};
};

type MenuContextType = ExposedMenu & {
    menuLoading: boolean,
    showProductInfo: (info: ProductInfoToShow) => void,
    searchQuery: string,
    setSearchQuery: (search: string) => void,
    lookup: MenuLookup,
};

const MenuContext = createContext<MenuContextType>({} as MenuContextType);

type Props = {
    children: ReactNode,
};

export const MenuProvider = ({children}: Props) => {
    const {dashboardLocation} = useRestaurant();
    const fulfillmentType = useFulfillmentType();
    const [showProductInfo, setShowProductInfo] = useState<ShowProductInfo>({
        show: false,
        product: null,
    });
    const [searchQuery, setSearchQuery] = useState("");
    const {data, dataUpdatedAt, isFetching} = useQuery({
        queryKey: ['menu', dashboardLocation.id, fulfillmentType],
        initialData: () => ({
            availableMenu: {
                restaurantId: dashboardLocation.id,
                fulfillmentType,
                categories: [],
                products: [],
                featuredProducts: [],
                loyaltyRewards: [],
                optionGroups: [],
                options: [],
            },
        }),
        queryFn: async () => {
            setShowProductInfo({
                show: false,
                product: null,
            });
            const menu = await api.menu.getMenu(dashboardLocation.id)
            return convertMenuToExposed(menu, fulfillmentType, dashboardLocation);
        },
        // staleTime: // TODO - 120 seconds stale
        refetchOnMount: true,
    });
    const availableMenu: AvailableMenu = useMemo(
        () => filterOnSearch(data.availableMenu, searchQuery),
        [data.availableMenu, searchQuery]);
    const lookup: MenuLookup = useMemo(
        () => createMenuLookup(availableMenu),
        [availableMenu]);

    return (
        <MenuContext.Provider
            value={{
                availableMenu: availableMenu,
                menuLoading: isFetching,
                searchQuery,
                setSearchQuery,
                showProductInfo: (info: ProductInfoToShow) => setShowProductInfo({...info, show: true,}),
                lookup,
            }}
        >
            {children}

            {
                !isFetching &&
                <ProductInfo
                    key={dataUpdatedAt}
                    show={showProductInfo.show}
                    availableMenu={data?.availableMenu}
                    setShow={(show: boolean) => setShowProductInfo((prev: ShowProductInfo) => ({...prev, show}))}
                    product={showProductInfo.product}
                    setProduct={(product: Product) => setShowProductInfo((prev: ShowProductInfo) => ({
                        ...prev,
                        product
                    }))}
                />
            }
        </MenuContext.Provider>
    );
}

const useMenu = () => {
    const ctx = useContext(MenuContext);
    if (!ctx) {
        throw new Error("useMenu must be used inside MenuProvider");
    }

    return ctx;
}

export default useMenu;
